import { getToolIcon } from "@/components/molecules/tool-icon";
import { Button, ConfirmDialog } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { Agent } from "@/features/agents/types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import type { AvailableTool } from "@/features/tools/types";
import { useTranslation } from "@/features/utils/i18n";
import { FormFooter } from "@/components/molecules/form-footer";
import { DynamicForm } from "@/views/backoffice/tools/components/dynamic-form";
import { useToolSettings } from "@/views/backoffice/tools/hooks/use-tool-settings";
import {
  ArrowCounterClockwiseIcon,
  ArrowSquareOutIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { sharedForAgent } from "./agent-skills";

/**
 * A module's settings, opened from the agent's page without leaving it.
 *
 * With one agent it is the company's form — the same one as the modules page,
 * since there is nobody to differ from — unless that agent still holds an
 * override of the module, which is then shown as such (`sharedForAgent`). With
 * several, every field shows the company's value and can be taken over for
 * this agent alone; the company's settings stay one link away.
 */
export function AgentSkillSheet({
  item,
  agent,
  soleAgent,
  onClose,
}: {
  item?: AvailableTool;
  agent: Agent;
  /** The only agent of the company: its settings are the company's. */
  soleAgent: boolean;
  onClose: () => void;
}) {
  const shared = !!item && sharedForAgent(item, agent.id, soleAgent);
  return (
    <Sheet open={!!item} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-xl">
        {item && (
          <SkillSettings
            // A different module or level is a different form: removing a
            // sole agent's override turns it back into the company's.
            key={`${item.tool.id}:${shared}`}
            item={item}
            agent={agent}
            shared={shared}
            soleAgent={soleAgent}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function SkillSettings({
  item,
  agent,
  shared,
  soleAgent,
}: {
  item: AvailableTool;
  agent: Agent;
  shared: boolean;
  soleAgent: boolean;
}) {
  const { t } = useTranslation("agents");
  const catalog = useToolCatalog();
  const company = useCurrentClient()?.client?.id;
  const Icon = getToolIcon(item.tool.name);
  const settings = useToolSettings({
    availableTool: item,
    agentId: shared ? undefined : agent.id,
  });
  const [confirmingReset, setConfirmingReset] = useState(false);

  return (
    <>
      <SheetHeader className="border-b p-6 pr-12">
        <div className="flex items-center gap-3">
          <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
            <Icon weight="fill" className="h-5 w-5" />
          </span>
          <div className="min-w-0 space-y-0.5">
            <SheetTitle>{catalog.label(item.tool)}</SheetTitle>
          </div>
        </div>
        <SheetDescription>{catalog.description(item.tool)}</SheetDescription>
        <p className="text-muted-foreground text-xs">
          {shared
            ? t("agents.skills.sheet.shared")
            : soleAgent
              ? t("agents.skills.sheet.soleOverride", { name: agent.name })
              : t("agents.skills.sheet.perAgent", { name: agent.name })}
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* It erases this agent's settings, so it says so and asks first:
              it read « Revenir aux réglages de l'entreprise », next to the
              link that does go to them, and reset on the first click. */}
          {!shared && settings.agentOverrideCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmingReset(true)}
            >
              <ArrowCounterClockwiseIcon className="h-4 w-4" />
              {t("agents.skills.sheet.reset")}
            </Button>
          )}
          {!shared && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/${company}/settings/tools/${item.tool.id}`}>
                <ArrowSquareOutIcon className="h-4 w-4" />
                {t("agents.skills.sheet.companySettings")}
              </Link>
            </Button>
          )}
        </div>
      </SheetHeader>

      <fieldset
        ref={settings.formRef}
        disabled={!settings.isActive}
        className="min-w-0 p-6 disabled:opacity-60"
      >
        <DynamicForm
          schema={catalog.schema(item.tool)}
          formData={settings.formData}
          onChange={settings.handleChange}
          customFields={settings.customFields}
          fieldVisibility={settings.fieldVisibility}
          fieldGroups={settings.fieldGroups}
          inheritable={!shared}
          parentData={settings.parentData}
        />
      </fieldset>

      {/* The same save bar as the modules page, at the foot of the sheet.
          Closing the sheet or opening another module changes the query
          string, so its leave guard watches that too. */}
      <FormFooter
        search
        dirty={settings.dirty}
        saving={settings.saving}
        onSave={settings.save}
        onCancel={settings.reset}
        className="mx-6 mb-4"
      />

      <ConfirmDialog
        open={confirmingReset}
        onOpenChange={setConfirmingReset}
        title={t("agents.skills.sheet.resetConfirm.title", {
          name: agent.name,
        })}
        description={[
          t("agents.skills.sheet.resetConfirm.description", {
            count: settings.agentOverrideCount,
            name: agent.name,
          }),
          // The form is emptied too, so an edit not yet saved goes with it.
          settings.dirty && t("agents.skills.sheet.resetConfirm.unsaved"),
        ]
          .filter(Boolean)
          .join(" ")}
        confirmLabel={t("agents.skills.sheet.resetConfirm.confirm")}
        onConfirm={() => void settings.resetToCompany()}
      />
    </>
  );
}
